import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { Box, AppBar, Toolbar, Typography, InputBase, Avatar, IconButton, Menu, MenuItem, Button, Divider, ListItemIcon, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Sidebar from '../main/Sidebar';
import { useNavigate } from 'react-router-dom';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';
import AdminSocketService from '../../services/AdminSocketService';

const MainLayout = ({ children }) => {
  const [selected, setSelected] = useState('dashboard');
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [badges, setBadges] = useState({
    pendingCompanies: 0,
    pendingFigures: 0,
    pendingFigureDatabaseDrafts: 0,
    pendingReports: 0,
  });
  const navigate = useNavigate();
  const auth = useContext(CONTEXTS.AuthContext);
  const api = useContext(CONTEXTS.APIContext);
  const adminController = useMemo(() => new CONTROLLERS.AdminController(api), [api]);
  const reportController = useMemo(() => new CONTROLLERS.ReportController(api), [api]);

  const loadBadges = useCallback(async () => {
    try {
      const [figureDraftStats, companyDrafts, reportStats] = await Promise.all([
        adminController.getFigureDraftStats(),
        adminController.getPendingCompanyDrafts({ skip: 0, take: 1 }),
        reportController.getReportStats(),
      ]);

      const nextBadges = {
        pendingCompanies: companyDrafts?.meta?.totalItems || 0,
        pendingFigures: figureDraftStats?.pending || 0,
        pendingFigureDatabaseDrafts: 0,
        pendingReports: reportStats?.pending || 0,
      };

      AdminSocketService.getInstance().setBadges(nextBadges);
    } catch (_) {}
  }, [adminController, reportController]);

  useEffect(() => {
    // Get current user from auth context
    if (auth.user) {
      setCurrentUser(auth.user);
    }
  }, [auth.user]);

  useEffect(() => {
    const socketService = AdminSocketService.getInstance();
    const offConn = socketService.onConnectionState(({ connected }) => {
      setSocketConnected(Boolean(connected));
    });
    const offBadges = socketService.onBadges((next) => {
      setBadges(next);
    });

    loadBadges();
    socketService.connect();

    return () => {
      try {
        offConn?.();
        offBadges?.();
      } catch (_) {}
    };
  }, [loadBadges]);

  const totalAlerts =
    (badges?.pendingCompanies || 0) +
    (badges?.pendingFigures || 0) +
    (badges?.pendingFigureDatabaseDrafts || 0) +
    (badges?.pendingReports || 0);

  // Helper functions to safely access user data
  const getDisplayName = () => {
    if (!currentUser) return 'Loading...';
    if (typeof currentUser.getDisplayName === 'function') {
      return currentUser.getDisplayName();
    }
    return currentUser.profile?.displayName || currentUser.username || 'User';
  };

  const getAvatar = () => {
    if (!currentUser) return null;
    if (typeof currentUser.getAvatar === 'function') {
      return currentUser.getAvatar();
    }
    return currentUser.profile?.currentAvatar?.url || null;
  };

  const getUserRole = () => {
    return currentUser?.role || 'user';
  };

  const getUserEmail = () => {
    return currentUser?.email || '';
  };

  const isPremium = () => {
    return currentUser?.isPremium || false;
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/settings');
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f7f9fb' }}>
      <Sidebar selected={selected} onSelect={setSelected} />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" elevation={0} color="transparent" sx={{ bgcolor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: 400, bgcolor: '#f4f6f8', borderRadius: 2, px: 2 }}>
              <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              <InputBase placeholder="Search here" fullWidth sx={{ py: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {getDisplayName()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                    {getUserRole() === 'admin' && (
                      <AdminPanelSettingsIcon sx={{ fontSize: 12, color: 'error.main' }} />
                    )}
                    {getUserRole() === 'moderator' && (
                      <AdminPanelSettingsIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {getUserRole()}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleMenu} size="small">
                  <Avatar 
                    alt={getDisplayName()} 
                    src={getAvatar()} 
                    sx={{ width: 40, height: 40 }}
                  >
                    {getDisplayName()?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
              </Box>
              <Menu 
                anchorEl={anchorEl} 
                open={Boolean(anchorEl)} 
                onClose={handleClose}
                PaperProps={{
                  sx: { width: 280, mt: 1.5 }
                }}
              >
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Avatar 
                      src={getAvatar()} 
                      sx={{ width: 48, height: 48 }}
                    >
                      {getDisplayName()?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {getDisplayName()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getUserEmail()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip 
                      label={getUserRole()} 
                      size="small" 
                      color={getUserRole() === 'admin' ? 'error' : getUserRole() === 'moderator' ? 'warning' : 'default'}
                      sx={{ textTransform: 'capitalize', height: 22, fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={socketConnected ? 'Socket Connected' : 'Socket Disconnected'}
                      size="small"
                      color={socketConnected ? 'success' : 'default'}
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                    {totalAlerts > 0 && (
                      <Chip
                        label={`Alerts ${totalAlerts}`}
                        size="small"
                        color="warning"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    )}
                    {isPremium() && (
                      <Chip 
                        label="Premium" 
                        size="small" 
                        color="success"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </Box>
                <MenuItem onClick={handleProfile} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  My Profile
                </MenuItem>
                <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
